import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from '../../core/toastr/toaster.service';
import { ModalService } from '../../core/modal/modal.service';
import { AuthService } from '../../core/services/auth.service';
import { LoginRequest, User } from '../../core/models/user';
import { NgxSpinnerService } from 'ngx-spinner';// Import service baru
import { UrlValidatorService } from '../../core/services/url-validator.service';
import { RecaptchaService } from '../../core/services/recaptcha.service';
import { environment } from '../../../environments/environment.development';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild('videoPlayer', { static: false }) videoPlayer!: ElementRef;
  @ViewChild('recaptchaContainer', { static: false }) recaptchaContainer!: ElementRef<HTMLElement>;

  email: string = '';
  password: string = '';
  showPassword: boolean = false;
  returnUrl: string = '/dashboard';
  loading: boolean = false;
  recaptchaSiteKey: string = environment.recaptchaSiteKey;
  reCaptchaToken: string = '';

  private toastrService = inject(ToasterService);
  private urlValidator = inject(UrlValidatorService);

  itemCurrentUser: User = {} as User;
  itemBusinessLine: string[] = [];
  itemPermissions: string[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private recaptchaService: RecaptchaService
  ) { }

  ngOnInit() {
    // Check if user is already logged in
    this.authService.checkAuthStatus().subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.router.navigate(['/dashboard']);
      }
    });

    // Get return url from route parameters and validate it
    const queryReturnUrl = this.route.snapshot.queryParams['returnUrl'];
    this.returnUrl = this.urlValidator.validateReturnUrl(queryReturnUrl);

    // Log jika ada attempt redirect yang diblokir
    if (queryReturnUrl && this.returnUrl !== queryReturnUrl) {
      console.warn('Invalid return URL blocked:', queryReturnUrl);
    }
  }

  playVideo() {
    const video = this.videoPlayer.nativeElement;
    if (video) {
      video.play()
        .catch((error: Error) => {
          console.log("Video play failed:", error);
        });
    }
  }

  async ngAfterViewInit() {
    // Pastikan video dimuat dengan muted
    const video = this.videoPlayer.nativeElement;
    video.muted = true;

    // Tunggu video loaded baru play
    video.addEventListener('loadedmetadata', () => {
      video.play()
        .catch((error: Error) => {
          console.log("Video autoplay failed:", error);
        });
    });

    try {
      await this.recaptchaService.render(this.recaptchaContainer.nativeElement);
    } catch (err) {
      console.error('Failed to render recaptcha', err);
    }
  }

  async onSubmit() {

    if (!this.email || !this.password) {
      return;
    }

    try {
      const loginData: LoginRequest = {
        email: this.email,
        password: this.password,
        captchaToken: this.recaptchaSiteKey
      };

      this.spinner.show();
      this.authService.login(loginData).subscribe({
        next: (response) => {
          this.spinner.hide();

          if (response.success) {
            // Update user data setelah login berhasil
            const userData = localStorage.getItem('currentUser');
            this.itemCurrentUser = userData ? JSON.parse(userData) : {} as User;
            this.itemBusinessLine = this.itemCurrentUser.business_lines || [];
            this.itemPermissions = this.itemCurrentUser.permissions || [];

            // Check user access
            if (!this.checkUserAccess()) {
              // Jika tidak memiliki akses, logout dan return
              const token = localStorage.getItem('token') || '';
              this.authService.logout(token);
              return;
            }

            // Jika memiliki akses, tampilkan pesan sukses dan navigasi
            this.toastrService.success('Login Berhasil', response.message || 'Selamat datang');

            // Validasi ulang returnUrl sebelum navigasi
            const queryReturnUrl = this.route.snapshot.queryParams['returnUrl'];
            const safeReturnUrl = this.urlValidator.validateReturnUrl(queryReturnUrl);

            // Navigasi ke URL yang sudah divalidasi
            this.router.navigate([safeReturnUrl]);
          } else {
            // Tampilkan pesan error dari API
            this.toastrService.error('Login Gagal', response.message || 'Terjadi kesalahan saat login');
          }
        },
        error: (error) => {
          this.spinner.hide();

          // Jika error adalah objek LoginResponse (dari catchError di service)
          if (error.success === false) {
            this.toastrService.error('Login Gagal', error.message || 'Silakan coba lagi');
          }
          // Jika error adalah HttpErrorResponse
          else if (error.status) {
            if (error.status === 400 || error.status === 401) {
              this.toastrService.error('Login Gagal', 'Email atau password salah');
            } else if (error.status === 500) {
              this.toastrService.error('Error', 'Terjadi kesalahan pada server');
            } else {
              this.toastrService.error('Error', 'Terjadi kesalahan saat login');
            }
          } else {
            // Fallback error message
            this.toastrService.error('Error Koneksi', 'Tidak dapat terhubung ke server');
          }

          console.error('Login error:', error);
        }
      });

    } catch (err) {
      this.toastrService.error('Please complete the reCAPTCHA', 'reCAPTCHA verification is required to proceed.');
      console.error(err);
    }

  }

  // Method untuk check permission dan business line
  private checkUserAccess(): boolean {
    const hasFrontendPermission = this.authPermissions('view frontend');
    const hasBusinessLine = this.itemBusinessLine.length > 0;

    if (!hasFrontendPermission && !hasBusinessLine) {
      // Tidak memiliki permission dan business line
      this.toastrService.error(
        'Access Denied',
        "You don't have permission and business line"
      );
      return false;
    } else if (!hasFrontendPermission) {
      // Tidak memiliki permission view frontend
      this.toastrService.error(
        'Access Denied',
        "Access denied. You don't have permission to access this system. Please contact the administrator."
      );
      return false;
    } else if (!hasBusinessLine) {
      // Tidak memiliki business line
      this.toastrService.error(
        'Access Denied',
        "Access denied. You don't have permission to access this system. Please contact the administrator."
      );
      return false;
    }

    return true; // User memiliki akses
  }

  authPermissions(permission: string): boolean {
    return this.itemPermissions.includes(permission);
  }

  authBusinessLine(businessLine: string): boolean {
    return this.itemBusinessLine.includes(businessLine);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}